import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { trainerLogin } from "@/api/trainerAuth";

export default function TrainerLogin() {
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);
  const navigate = useNavigate();

  const submit = async (e) => {
    e.preventDefault();
    setError("");
    setLoading(true);

    try {
      await trainerLogin({ email, password });
      navigate("/trainer", { replace: true });
    } catch {
      setError("بيانات الدخول غير صحيحة");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div style={styles.container}>
      <div style={styles.card}>
        <div style={styles.header}>
          <div style={styles.logoContainer}>
            <div style={styles.logoIcon}>🏋️</div>
            <h1 style={styles.systemName}>نظام إدارة المدرّبين</h1>
          </div>
          <h2 style={styles.title}>تسجيل دخول المدرّب</h2>
          <p style={styles.subtitle}>أدخل بياناتك للوصول إلى لوحة التحكم</p>
        </div>

        <form onSubmit={submit} style={styles.form}>
          <div style={styles.inputGroup}>
            <label htmlFor="email" style={styles.label}>البريد الإلكتروني</label>
            <div style={styles.inputWithIcon}>
              <span style={styles.inputIcon}>📧</span>
              <input
                id="email"
                type="email"
                placeholder="example@trainer.com"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                required
                style={styles.input}
                dir="ltr"
              />
            </div>
          </div>

          <div style={styles.inputGroup}>
            <label htmlFor="password" style={styles.label}>كلمة المرور</label>
            <div style={styles.inputWithIcon}>
              <span style={styles.inputIcon}>🔒</span>
              <input
                id="password"
                type="password"
                placeholder="أدخل كلمة المرور"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                required
                style={styles.input}
              />
            </div>
          </div>

          <div style={styles.formOptions}>
            <label style={styles.rememberMe}>
              <input type="checkbox" style={styles.checkbox} />
              <span>تذكرني</span>
            </label>
            <a href="/forgot-password" style={styles.forgotPassword}>
              نسيت كلمة المرور؟
            </a>
          </div>

          {error && (
            <div style={styles.errorMessage}>
              <span style={styles.errorIcon}>⚠️</span>
              {error}
            </div>
          )}

          <button 
            type="submit" 
            style={loading ? {...styles.submitButton, ...styles.submitButtonDisabled} : styles.submitButton}
            disabled={loading}
          >
            {loading ? (
              <>
                <span style={styles.spinner}></span>
                جاري الدخول...
              </>
            ) : (
              "دخول إلى لوحة التحكم"
            )}
          </button>

          <div style={styles.divider}>
            <span>أو</span>
          </div>

          <div style={styles.additionalOptions}>
            <p style={styles.registerLink}>
              ليس لديك حساب؟ <a href="/trainer/register" style={styles.registerLinkA}>سجّل كمدرّب جديد</a>
            </p>
            <a href="/" style={styles.backToHome}>
              ← العودة إلى الصفحة الرئيسية
            </a>
          </div>
        </form>

        <div style={styles.footer}>
          <p style={styles.footerText}>© 2024 نظام إدارة المدرّبين. جميع الحقوق محفوظة.</p>
          <div style={styles.footerLinks}>
            <a href="/privacy" style={styles.footerLink}>سياسة الخصوصية</a>
            <a href="/terms" style={styles.footerLink}>الشروط والأحكام</a>
            <a href="/support" style={styles.footerLink}>الدعم الفني</a>
          </div>
        </div>
      </div>

      <style>{`
        @keyframes slideUp {
          from {
            opacity: 0;
            transform: translateY(30px);
          }
          to {
            opacity: 1;
            transform: translateY(0);
          }
        }
        
        @keyframes spin {
          to { transform: rotate(360deg); }
        }
      `}</style>
    </div>
  );
}

const styles = {
  container: {
    minHeight: '100vh',
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center',
    background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
    padding: '20px',
    fontFamily: "'Segoe UI', Tahoma, Geneva, Verdana, sans-serif",
    direction: 'rtl',
  },
  card: {
    background: 'white',
    borderRadius: '20px',
    boxShadow: '0 20px 60px rgba(0, 0, 0, 0.3)',
    width: '100%',
    maxWidth: '450px',
    overflow: 'hidden',
    animation: 'slideUp 0.6s ease-out',
  },
  header: {
    background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
    color: 'white',
    padding: '40px 30px',
    textAlign: 'center',
  },
  logoContainer: {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    gap: '15px',
    marginBottom: '20px',
  },
  logoIcon: {
    fontSize: '48px',
    background: 'rgba(255, 255, 255, 0.2)',
    width: '80px',
    height: '80px',
    borderRadius: '50%',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    backdropFilter: 'blur(10px)',
  },
  systemName: {
    fontSize: '1.5rem',
    margin: '0',
    fontWeight: '600',
  },
  title: {
    fontSize: '1.8rem',
    margin: '10px 0',
    fontWeight: '700',
  },
  subtitle: {
    fontSize: '0.9rem',
    opacity: '0.9',
    marginTop: '5px',
  },
  form: {
    padding: '40px',
  },
  inputGroup: {
    marginBottom: '25px',
  },
  label: {
    display: 'block',
    marginBottom: '8px',
    fontWeight: '600',
    color: '#333',
    fontSize: '0.9rem',
  },
  inputWithIcon: {
    position: 'relative',
  },
  inputIcon: {
    position: 'absolute',
    right: '15px',
    top: '50%',
    transform: 'translateY(-50%)',
    color: '#667eea',
    fontSize: '1.2rem',
  },
  input: {
    width: '100%',
    padding: '15px 50px 15px 20px',
    border: '2px solid #e0e0e0',
    borderRadius: '10px',
    fontSize: '1rem',
    transition: 'all 0.3s ease',
    boxSizing: 'border-box',
  },
  formOptions: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    marginBottom: '25px',
  },
  rememberMe: {
    display: 'flex',
    alignItems: 'center',
    gap: '8px',
    cursor: 'pointer',
    color: '#555',
    fontSize: '0.9rem',
  },
  checkbox: {
    width: '18px',
    height: '18px',
    accentColor: '#667eea',
  },
  forgotPassword: {
    color: '#667eea',
    textDecoration: 'none',
    fontSize: '0.9rem',
    fontWeight: '500',
    transition: 'color 0.3s ease',
  },
  errorMessage: {
    background: '#fee',
    color: '#c33',
    padding: '12px 15px',
    borderRadius: '8px',
    marginBottom: '20px',
    display: 'flex',
    alignItems: 'center',
    gap: '10px',
    fontSize: '0.9rem',
    borderRight: '4px solid #c33',
  },
  errorIcon: {
    fontSize: '1.2rem',
  },
  submitButton: {
    width: '100%',
    padding: '16px',
    background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
    color: 'white',
    border: 'none',
    borderRadius: '10px',
    fontSize: '1.1rem',
    fontWeight: '600',
    cursor: 'pointer',
    transition: 'all 0.3s ease',
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center',
    gap: '10px',
    marginBottom: '20px',
  },
  submitButtonDisabled: {
    opacity: '0.7',
    cursor: 'not-allowed',
  },
  spinner: {
    width: '20px',
    height: '20px',
    border: '3px solid rgba(255, 255, 255, 0.3)',
    borderRadius: '50%',
    borderTopColor: 'white',
    animation: 'spin 1s ease-in-out infinite',
  },
  divider: {
    display: 'flex',
    alignItems: 'center',
    margin: '25px 0',
    color: '#777',
  },
  additionalOptions: {
    textAlign: 'center',
  },
  registerLink: {
    color: '#555',
    marginBottom: '15px',
    fontSize: '0.95rem',
  },
  registerLinkA: {
    color: '#667eea',
    textDecoration: 'none',
    fontWeight: '600',
    transition: 'color 0.3s ease',
  },
  backToHome: {
    display: 'inline-block',
    color: '#777',
    textDecoration: 'none',
    fontSize: '0.9rem',
    transition: 'color 0.3s ease',
  },
  footer: {
    background: '#f8f9fa',
    padding: '20px 30px',
    textAlign: 'center',
    borderTop: '1px solid #e0e0e0',
  },
  footerText: {
    color: '#666',
    fontSize: '0.8rem',
    margin: '0 0 10px 0',
  },
  footerLinks: {
    display: 'flex',
    justifyContent: 'center',
    gap: '20px',
  },
  footerLink: {
    color: '#667eea',
    textDecoration: 'none',
    fontSize: '0.8rem',
    transition: 'color 0.3s ease',
  },
};

// إضافة hover effects عبر JavaScript
const addHoverEffects = () => {
  const style = document.createElement('style');
  style.textContent = `
    .trainer-input:focus {
      outline: none;
      border-color: #667eea !important;
      box-shadow: 0 0 0 3px rgba(102, 126, 234, 0.1) !important;
    }
    
    .forgot-password-link:hover {
      color: #764ba2 !important;
      text-decoration: underline !important;
    }
    
    .submit-btn:hover:not(:disabled) {
      transform: translateY(-2px) !important;
      box-shadow: 0 10px 20px rgba(102, 126, 234, 0.3) !important;
    }
    
    .register-link-a:hover {
      color: #764ba2 !important;
      text-decoration: underline !important;
    }
    
    .back-to-home-link:hover {
      color: #667eea !important;
    }
    
    .footer-link:hover {
      color: #764ba2 !important;
      text-decoration: underline !important;
    }
    
    input::placeholder {
      color: #999 !important;
    }
    
    @media (max-width: 480px) {
      .trainer-login-card {
        border-radius: 15px !important;
      }
      
      .login-form {
        padding: 30px 20px !important;
      }
      
      .login-header {
        padding: 30px 20px !important;
      }
      
      .system-name {
        font-size: 1.3rem !important;
      }
      
      .login-title {
        font-size: 1.5rem !important;
      }
      
      .form-options {
        flex-direction: column !important;
        gap: 15px !important;
        align-items: flex-start !important;
      }
      
      .footer-links {
        flex-direction: column !important;
        gap: 10px !important;
      }
    }
  `;
  document.head.appendChild(style);
};

// إضافة تأثيرات hover عند تحميل المكون
if (typeof window !== 'undefined') {
  setTimeout(addHoverEffects, 100);
}